import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SignupModal from '../components/SignupModal';
import { Button } from '../components/ui/button';
import { ArrowRight, Play, CheckCircle2, Sparkles, Zap, TrendingUp, Shield, BarChart3, Users2, Target, Clock, Award } from 'lucide-react';
import { Toaster } from '../components/ui/toaster';

const Home = () => {
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: Sparkles,
      title: 'AI-Powered Lead Scoring',
      description: 'Automatically prioritize leads using advanced machine learning algorithms that learn from your conversion patterns.',
    },
    {
      icon: TrendingUp,
      title: 'Real-Time Analytics',
      description: 'Track campaign performance with live dashboards and get actionable insights to optimize your strategy.',
    },
    {
      icon: Zap,
      title: 'Automated Workflows',
      description: 'Set up intelligent automation that nurtures leads through the funnel without manual intervention.',
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-level encryption and compliance with GDPR, CCPA, and SOC 2 Type II standards.',
    },
    {
      icon: BarChart3,
      title: 'Advanced Reporting',
      description: 'Generate comprehensive reports with customizable metrics and export data for deeper analysis.',
    },
    {
      icon: Users2,
      title: 'Team Collaboration',
      description: 'Work seamlessly with your team using shared workspaces, comments, and real-time notifications.',
    },
  ];

  const stats = [
    { icon: Users2, value: '300+', label: 'Active Users' },
    { icon: TrendingUp, value: '5M+', label: 'Leads Processed' },
    { icon: Target, value: '98%', label: 'Satisfaction Rate' },
    { icon: Award, value: '4.9/5', label: 'User Rating' },
  ];

  const useCases = [
    {
      title: 'Marketing Teams',
      description: 'Generate and qualify leads at scale with AI-powered automation',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
    },
    {
      title: 'Sales Teams',
      description: 'Close deals faster with intelligent lead prioritization and insights',
      image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80',
    },
    {
      title: 'Enterprise',
      description: 'Scale your operations with enterprise-grade features and support',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80',
    },
  ];

  const testimonials = [
    {
      quote: "AALIGN transformed how we manage leads. Our conversion rate increased by 247% in just 3 months.",
      author: "Sarah Johnson",
      role: "VP Marketing, TechCorp",
      avatar: "https://i.pravatar.cc/150?img=5",
    },
    {
      quote: "The AI-powered lead scoring is a game changer. We now focus on the leads that actually convert.",
      author: "Michael Chen",
      role: "Sales Director, GrowthLabs",
      avatar: "https://i.pravatar.cc/150?img=12",
    },
    {
      quote: "Best ROI we've seen from any marketing tool. The automation alone saves us 20+ hours per week.",
      author: "Emily Davis",
      role: "CMO, ScaleUp Inc",
      avatar: "https://i.pravatar.cc/150?img=9",
    },
  ];

  const latestBlogs = [
    {
      id: 1,
      title: 'The Future of Lead Management: AI-Powered Insights for 2026',
      excerpt: 'Discover how artificial intelligence is transforming lead management and what it means for your business.',
      author: 'Sarah Johnson',
      date: 'Jan 8, 2026',
      readTime: '8 min read',
      category: 'Industry Insights',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop',
    },
    {
      id: 2,
      title: '5 Lead Validation Mistakes That Are Costing You Money',
      excerpt: 'Learn about the most common lead validation errors and how to avoid them to maximize your ROI.',
      author: 'Michael Chen',
      date: 'Jan 5, 2026',
      readTime: '6 min read',
      category: 'Best Practices',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop',
    },
    {
      id: 3,
      title: 'How to Integrate AALIGN with Your Existing CRM',
      excerpt: 'A step-by-step guide to seamlessly connecting AALIGN with HubSpot, Salesforce, and more.',
      author: 'Emily Rodriguez',
      date: 'Jan 2, 2026',
      readTime: '10 min read',
      category: 'Tutorials',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Toaster />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-red-50"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-orange-200 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-200 rounded-full blur-3xl opacity-20"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 px-5 py-2.5 rounded-full text-sm font-bold mb-8 shadow-sm">
              <Sparkles className="w-4 h-4" />
              AI-Powered Lead Management Platform
            </div>
            <h1 className="text-5xl lg:text-7xl font-black mb-8 leading-tight">
              Turn Leads Into
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-orange-600 to-red-600">
                Revenue
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 mb-10 leading-relaxed">
              The only lead management platform you need to capture, nurture, and convert high-quality B2B leads at scale.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={() => setIsSignupModalOpen(true)}
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold px-10 py-7 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Link to="/contact-us">
                <Button
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50 font-bold px-10 py-7 text-lg rounded-xl transition-all"
                >
                  <Play className="mr-2 w-5 h-5" />
                  Book Demo
                </Button>
              </Link>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Free 30-day trial • No credit card required • Cancel anytime
            </p>
          </div>

          {/* Hero Image/Dashboard Preview */}
          <div className="relative max-w-6xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-600 rounded-3xl blur-2xl opacity-20 transform scale-105"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl p-4 border-8 border-gray-100">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80"
                alt="AALIGN Dashboard"
                className="w-full rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-orange-500 to-red-600">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="text-center text-white">
                  <Icon className="w-12 h-12 mx-auto mb-4 opacity-90" />
                  <div className="text-4xl lg:text-5xl font-black mb-2">{stat.value}</div>
                  <div className="text-orange-100 font-semibold text-lg">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              Everything You Need to
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600"> Scale Growth</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful features designed to help B2B businesses capture, manage, and convert more leads.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  className="group p-8 rounded-2xl bg-white border-2 border-gray-100 hover:border-orange-200 hover:shadow-2xl transition-all duration-300 cursor-pointer"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              Built For Modern
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600"> Teams</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're in marketing, sales, or enterprise, AALIGN adapts to your workflow.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {useCases.map((useCase, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-3xl shadow-xl cursor-pointer transform hover:scale-105 transition-all duration-500"
              >
                <div className="aspect-[4/3] relative">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">{useCase.title}</h3>
                  <p className="text-gray-200">{useCase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              Loved By
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600"> 300+ Businesses</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See what our customers have to say about their experience with AALIGN.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 hover:border-orange-200 hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.author}
                    className="w-14 h-14 rounded-full"
                  />
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Blog Posts Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 className="text-4xl lg:text-5xl font-black mb-4">
                Latest From Our
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600"> Blog</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl">
                Insights, tutorials, and best practices to help you master lead management.
              </p>
            </div>
            <Link to="/blog" className="mt-6 md:mt-0">
              <Button variant="outline" className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-semibold">
                View All Articles
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {latestBlogs.map((post) => (
              <Link to="/blog" key={post.id}>
                <article className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group h-full">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        post.category === 'Industry Insights' ? 'bg-blue-100 text-blue-700' :
                        post.category === 'Best Practices' ? 'bg-green-100 text-green-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-sm text-gray-600">{post.author}</span>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </div>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl lg:text-6xl font-black mb-6">
            Ready to Transform Your Lead Management?
          </h2>
          <p className="text-xl text-orange-100 mb-10 leading-relaxed">
            Join thousands of businesses already growing with AALIGN. Start your free trial today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => setIsSignupModalOpen(true)}
              className="bg-white text-orange-600 hover:bg-gray-100 font-bold px-12 py-7 text-lg rounded-xl shadow-2xl hover:shadow-3xl transition-all transform hover:scale-105"
            >
              Start Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Link to="/contact-us">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 font-bold px-12 py-7 text-lg rounded-xl transition-all"
              >
                Contact Sales
              </Button>
            </Link>
          </div>
          <p className="text-sm text-orange-100 mt-6">
            💳 No credit card required • ⚡ Set up in 2 minutes • 🎯 Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
      
      {/* Signup Modal */}
      <SignupModal isOpen={isSignupModalOpen} onClose={() => setIsSignupModalOpen(false)} />
    </div>
  );
};

export default Home;